/**
 * OneTimeLinkDialog
 *
 * Shows a one-time invite/reset link the backend just issued: readonly link + copy button,
 * an email-sent badge, the expiry, and a "shown once" warning. Reused by every flow that mints
 * a link — create user, resend invite, admin password reset, unarchive, AZURE→LOCAL switch.
 *
 * `OneTimeLinkPanel` is the body on its own, for embedding inside another dialog (the add-user
 * dialog shows it as its result step); `OneTimeLinkDialog` is the standalone controlled dialog.
 */

import React, { useEffect, useState } from "react";
import { AlertTriangle, Check, Copy, MailCheck, MailX } from "lucide-react";

import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";

export type OneTimeLink = {
  /** Dialog heading, e.g. "Invite created" / "Reset link created". */
  title: string;
  /** Short context line under the title, e.g. who the link is for. */
  description?: string;
  link: string;
  emailSent?: boolean;
  expiresInMinutes?: number;
};

/** "Link expires in 24 hours" / "Link expires in 30 minutes". */
function formatExpiry(expiresInMinutes: number): string {
  if (expiresInMinutes >= 60 && expiresInMinutes % 60 === 0) {
    const hours = expiresInMinutes / 60;
    return `Link expires in ${hours} hour${hours === 1 ? "" : "s"}`;
  }
  return `Link expires in ${expiresInMinutes} minute${expiresInMinutes === 1 ? "" : "s"}`;
}

export function OneTimeLinkPanel({
  link,
  emailSent,
  expiresInMinutes,
}: Pick<OneTimeLink, "link" | "emailSent" | "expiresInMinutes">) {
  const [copied, setCopied] = useState(false);

  // Reset the copied feedback when a different link is shown.
  useEffect(() => setCopied(false), [link]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard can be unavailable (permissions, insecure context); the input stays selectable.
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Input
          readOnly
          value={link}
          onFocus={(e) => e.target.select()}
          className="font-mono text-xs"
        />
        <Button variant="outline" size="icon" className="shrink-0" onClick={copy}>
          {copied ? <Check className="size-4 text-emerald-600" /> : <Copy className="size-4" />}
          <span className="sr-only">Copy link</span>
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {emailSent ? (
          <Badge variant="outline" className="text-emerald-600 border-emerald-200 font-normal">
            <MailCheck className="size-3 mr-1" />
            Invite email sent
          </Badge>
        ) : (
          <Badge variant="outline" className="text-amber-600 border-amber-200 font-normal">
            <MailX className="size-3 mr-1" />
            Email not configured — share this link manually
          </Badge>
        )}
        {typeof expiresInMinutes === "number" && expiresInMinutes > 0 ? (
          <span className="text-xs text-muted-foreground">{formatExpiry(expiresInMinutes)}</span>
        ) : null}
      </div>

      <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-500/5 p-3 text-xs text-amber-700 dark:text-amber-500">
        <AlertTriangle className="size-4 shrink-0" />
        <span>This link is shown once — copy it now if email is not configured.</span>
      </div>
    </div>
  );
}

export function OneTimeLinkDialog({
  link,
  onClose,
}: {
  /** The link to show; null renders a closed dialog. */
  link: OneTimeLink | null;
  onClose: () => void;
}) {
  return (
    <Dialog open={!!link} onOpenChange={(o) => !o && onClose()}>
      {link ? (
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{link.title}</DialogTitle>
            {link.description ? <DialogDescription>{link.description}</DialogDescription> : null}
          </DialogHeader>
          <OneTimeLinkPanel
            link={link.link}
            emailSent={link.emailSent}
            expiresInMinutes={link.expiresInMinutes}
          />
          <DialogFooter>
            <Button onClick={onClose}>Done</Button>
          </DialogFooter>
        </DialogContent>
      ) : null}
    </Dialog>
  );
}
